/**
 * Merges custom template configuration with default template layout
 * Custom config overrides specific fields while preserving defaults
 */
export const mergeTemplateConfig = (defaultTemplate, customConfig) => {
  if (!customConfig) return defaultTemplate;

  return {
    ...defaultTemplate,
    layout: {
      ...defaultTemplate.layout,
      ...customConfig.layout,
    },
    fieldVisibility: {
      ...customConfig.fieldVisibility,
    },
  };
};

/**
 * Creates a default configuration structure for a template
 * Used when user first edits a template
 */
export const createDefaultConfig = (template, templateKey = '') => {
  return {
    templateId: template.cardWidth + 'x' + template.cardHeight,
    templateKey,
    layout: {
      titleFontSize: template.layout.titleFontSize,
      titleFontStyle: template.layout.titleFontStyle,
      titleX: template.layout.titleX ?? template.layout.contentStartX,
      titleY: template.layout.titleY,
      
      artistFontSize: template.layout.artistFontSize,
      artistFontStyle: template.layout.artistFontStyle,
      artistX: template.layout.artistX ?? template.layout.contentStartX,
      artistY: template.layout.artistY,
      
      infoFontSize: template.layout.infoFontSize,
      infoFontStyle: template.layout.infoFontStyle,
      
      mediumX: template.layout.mediumX ?? template.layout.contentStartX,
      mediumY: template.layout.mediumY,
      completedDateX: template.layout.completedDateX ?? template.layout.contentStartX,
      completedDateY: template.layout.completedDateY ?? template.layout.yearY,
      yearY: template.layout.yearY,
      dimensionsX: template.layout.dimensionsX ?? template.layout.contentStartX,
      dimensionsY: template.layout.dimensionsY,
      priceX: template.layout.priceX ?? template.layout.contentStartX,
      priceY: template.layout.priceY,
      priceFontSize:
        template.layout.priceFontSize ||
        template.layout.infoFontSize ||
        template.layout.titleFontSize ||
        14,
      
      // Field positions
      contentStartX: template.layout.contentStartX,
      contentEndX: template.layout.contentEndX,
      
      // QR Code
      qrCodeSize: template.layout.qrCodeSize,
      qrCodeX: template.layout.qrCodeX,
      qrCodeY: template.layout.qrCodeY,
      
      // Artwork number position (new)
      artworkNumberX: template.layout.artworkNumberX || template.layout.qrCodeX,
      artworkNumberY: template.layout.artworkNumberY || (template.layout.qrCodeY - 0.3),
      artworkNumberFontSize: template.layout.artworkNumberFontSize || 8,
      
      // Year/completed date font size
      yearFontSize: template.layout.yearFontSize || template.layout.infoFontSize || 10,
      
      // Dimensions font size
      dimensionsFontSize: template.layout.dimensionsFontSize || template.layout.infoFontSize || 10,
      
      // Prints Available
      printsAvailableX: template.layout.printsAvailableX ?? (template.cardWidth - 1.2),
      printsAvailableY: template.layout.printsAvailableY ?? (template.cardHeight - 0.2),
      printsAvailableFontSize: template.layout.printsAvailableFontSize || 9,
      
      // Audio Story Icon
      audioIconX: template.layout.audioIconX ?? (template.cardWidth - 0.35),
      audioIconY: template.layout.audioIconY ?? 0.15,
      audioIconSize: template.layout.audioIconSize || 0.2,
    },
    fieldVisibility: {
      showTitle: true,
      showArtist: true,
      showQRCode: true,
      showArtworkNumber: true,
      showPrice: true,
      showMedium: true,
      showCompletedDate: true,
      showDimensions: true,
      showCutLines: false,
      showPrintsAvailable: true,
      showAudioStory: true,
    },
  };
};

const NUMERIC_LAYOUT_FIELDS = [
  'titleFontSize',
  'titleX',
  'titleY',
  'artistFontSize',
  'artistX',
  'artistY',
  'infoFontSize',
  'mediumX',
  'mediumY',
  'completedDateX',
  'completedDateY',
  'yearY',
  'yearFontSize',
  'dimensionsX',
  'dimensionsY',
  'dimensionsFontSize',
  'priceX',
  'priceY',
  'priceFontSize',
  'contentStartX',
  'contentEndX',
  'qrCodeSize',
  'qrCodeX',
  'qrCodeY',
  'artworkNumberX',
  'artworkNumberY',
  'artworkNumberFontSize',
  'printsAvailableX',
  'printsAvailableY',
  'printsAvailableFontSize',
  'audioIconX',
  'audioIconY',
  'audioIconSize',
];

const coerceNumericLayoutValues = (layout, fallbacks = {}) => {
  const coerced = { ...layout };
  NUMERIC_LAYOUT_FIELDS.forEach((field) => {
    if (coerced[field] !== undefined) {
      const numericValue = typeof coerced[field] === 'number' ? coerced[field] : parseFloat(coerced[field]);
      coerced[field] = Number.isFinite(numericValue) ? numericValue : fallbacks[field];
    } else if (fallbacks[field] !== undefined) {
      coerced[field] = fallbacks[field];
    }
  });
  return coerced;
};

const buildLayoutFromLegacyConfig = (legacyConfig, defaultLayout) => {
  const layout = { ...defaultLayout };

  if (legacyConfig.positions) {
    Object.assign(layout, legacyConfig.positions);
  }

  if (legacyConfig.fontSizes) {
    layout.titleFontSize = legacyConfig.fontSizes.titleFontSize ?? layout.titleFontSize;
    layout.artistFontSize = legacyConfig.fontSizes.artistFontSize ?? layout.artistFontSize;
    layout.infoFontSize = legacyConfig.fontSizes.infoFontSize ?? layout.infoFontSize;
    layout.priceFontSize = legacyConfig.fontSizes.priceFontSize ?? layout.priceFontSize;
  }

  return layout;
};

/**
 * Normalize template config to ensure layout/visibility fields exist and numeric values are valid
 */
export const normalizeTemplateConfig = (template, config, templateKey = '') => {
  if (!config || !template) return null;

  const defaultConfig = createDefaultConfig(template, templateKey || config.templateKey || '');
  const defaultLayout = defaultConfig.layout;

  let layout = defaultLayout;

  if (config.layout) {
    layout = {
      ...defaultLayout,
      ...config.layout,
    };
  } else if (config.positions || config.fontSizes) {
    layout = buildLayoutFromLegacyConfig(config, defaultLayout);
  }

  const normalizedLayout = coerceNumericLayoutValues(layout, defaultLayout);

  const fieldVisibility = {
    ...defaultConfig.fieldVisibility,
    ...(config.fieldVisibility || config.visibility || {}),
  };

  return {
    ...defaultConfig,
    ...config,
  templateKey: templateKey || config.templateKey || defaultConfig.templateKey,
    layout: normalizedLayout,
    fieldVisibility,
  };
};

/**
 * Validate if configuration is properly structured
 */
export const isValidConfig = (config) => {
  return (
    config &&
    config.layout &&
    typeof config.layout.titleFontSize === 'number' &&
    config.fieldVisibility &&
    typeof config.fieldVisibility === 'object'
  );
};
